import { useRef, useState } from 'react';
import { Rating } from 'react-simple-star-rating';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import ProductRatingFormValidator from './ProductRatingFormValidator';
import flashMessage from '../../utils/flashMessage';
import ReCAPTCHA from 'react-google-recaptcha';

import './ProductRatingForm.css';
import { MultipleImageUpload } from '../MultipleImageUpload';
import { ReCAPTCHAProps } from './Recaptcha.type';
type ErrorResponse = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

function ProductRatingForm(props: any) {
  const [loading, setLoading] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHAProps>(null);
  const {
    productTitle,
    productImage,
    productId,
    csrfToken,
    closeModal,
    siteKey,
  } = props;

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    resolver: ProductRatingFormValidator,
  });

  const handleRating = (rate: number) => {
    setValue('rating', rate);
    setError('rating', { message: '' });
  };

  const submitForm = async (values: any) => {
    setLoading(true);
    const token = (await recaptchaRef?.current?.executeAsync()) || '';

    if (values.captchaToken !== undefined || values.captchaToken !== null) {
      const ratingFormData = new FormData();
      ratingFormData.append('rating', values.rating);
      ratingFormData.append('name', values.name);
      ratingFormData.append('email', values.email);
      ratingFormData.append('title', values.title);
      ratingFormData.append('city', values.city);
      ratingFormData.append('comment', values.comment);
      ratingFormData.append('product_id', productId);
      ratingFormData.append('g-recaptcha-response', token);
      ratingFormData.append('csrf_token', csrfToken);
      for (let i = 0; i < values.images.length; i++) {
        ratingFormData.append(`images[${i}]`, values.images[i]);
      }

      try {
        await axios({
          method: 'post',
          url: '/api/v1/ratings/rate',
          data: ratingFormData,
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then((response: { status: number }) => {
          if (response.status === 201) {
            closeModal();
            flashMessage(
              'success',
              'Obrigado por sua avaliação!',
              'Ela será analisada e publicada em breve'
            );
          } else {
            flashMessage(
              'error',
              'Ocorreu um erro ao enviar sua avaliação!',
              'Tente novamente.'
            );
          }
          setLoading(false);
        });
      } catch (error: unknown | ErrorResponse) {
        setLoading(false);
        const message = (error as ErrorResponse)?.response?.data?.message;
        if (message) {
          flashMessage('error', message, '');
        }
      }
    } else {
      setLoading(false);
    }
  };

  const hasImage = productImage.split("/").pop() !== '';

  return (
    <div id="product-rating-modal-body">
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />

      <div id="product-infos">
        {hasImage ? (
        <img
          src={productImage}
          style={{ width: '50px', height: '50px' }}
          className="product-infos-image"
          alt={productTitle}
        />
        ) : null}
        <p className="product-infos-title">{productTitle}</p>
      </div>
      <div id="box-rating-stars">
        <p>Clique nas estrelas para avaliar *</p>
        <Rating
          onClick={handleRating}
          showTooltip={true}
          tooltipDefaultText="Avalie"
          SVGstrokeColor="#EC9D40"
          SVGstorkeWidth={1.5}
          fillColor="#EC9D40"
          emptyColor="#ffffff"
          titleSeparator="de"
          tooltipArray={['Ruim', 'Regular', 'Bom', 'Ótimo', 'Excelente']}
        />
        {errors?.rating?.message ? (
          <p id="rating-error" className="error-message">
            {errors.rating.message}
          </p>
        ) : null}
      </div>
      <div id="form-rating">
        <form onSubmit={handleSubmit(submitForm)}>
          <div className="form-inputs">
            <div className="form-input">
              <p className="label-form-inputs">Seu nome *</p>
              <input
                type="text"
                id="rating-name"
                {...register('name')}
                className={errors.name ? 'error' : ''}
              />
              {errors.name ? (
                <p className="error-message">{errors.name.message}</p>
              ) : null}
            </div>
            <div className="form-input">
              <p className="label-form-inputs">Seu e-mail *</p>
              <input
                type="text"
                id="rating-email"
                {...register('email')}
                className={errors.email ? 'error' : ''}
              />
              {errors?.email?.message ? (
                <p className="error-message">{errors.email.message}</p>
              ) : null}
            </div>
            <div className="form-input">
              <p className="label-form-inputs">Título do comentário *</p>
              <input
                type="text"
                id="rating-title"
                {...register('title')}
                className={errors.title ? 'error' : ''}
              />
              {errors.title ? (
                <p className="error-message">{errors.title.message}</p>
              ) : null}
            </div>
            <div className="form-input">
              <p className="label-form-inputs">Sua cidade *</p>
              <input
                type="text"
                id="rating-city"
                {...register('city')}
                className={errors.city ? 'error' : ''}
              />
              {errors.city ? (
                <p className="error-message">{errors.city.message}</p>
              ) : null}
            </div>
          </div>

          <div className="form-input-comment">
            <p className="label-form-inputs">Comentário *</p>
            <textarea
              id="rating-comment"
              {...register('comment')}
              className={errors.comment ? 'error' : ''}
            />
            {errors.comment ? (
              <p className="error-message">{errors.comment.message}</p>
            ) : null}
          </div>

          <div className="form-input-comment">
            <MultipleImageUpload
              register={register}
              setValue={setValue}
              setError={setError}
              name="images"
              label="Foto do produto <span>(opcional)</span>"
            />
          </div>
          <div id="submit-rating-form">
            <button
              type="submit"
              id="btn-submit-rating"
              className="btn rating-btn "
              disabled={loading}
            >
              <span className="btn-text">
                {loading ? 'Enviando...' : 'Enviar avaliação'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProductRatingForm;
